import { FunctionComponent, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, Navigate, useParams } from 'react-router-dom';
import PaymentSuccess from './pages/payment/PaymentSuccess';
import PaymentCancel from './pages/payment/PaymentCancel';
import {
  Landing,
  Login,
  Signup,
  VerifyEmail,
  AdminUsers,
  AdminHealth,
  AdminAgent,
  AdminControlCenter,
  ChypsQuestionnaire,
  ChypsIntake,
  Settings,
  Feedback,
  Dashboard,
  ModalityPlaceholder,
  BiomarkerCenterPage,
  SymptomsProfilePage,
  ClinicalMarketingPage,
  ResearchPortalPage,
  ResearchProjectEditorPage,
} from './pages';
import LogoPreview from './pages/LogoPreview';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import NeuroIQHub from './pages/neuroiq';
import GolfMarker from './pages/neuroiq/GolfMarker';
import HomeworkPage from './pages/homework/HomeworkPage';
import VideoDemoLibrary from './pages/VideoDemoLibrary';
import VideoRedactor from './pages/VideoRedactor';
import AuthGuard from './components/AuthGuard';
import RequireSuperAdmin from './components/RequireSuperAdmin';
import AppShellWrapper from './components/AppShellWrapper';
import SessionRecorder from './components/SessionRecorder';
import { ErrorBoundary } from './components/ErrorBoundary';
import { store } from './store';
import { pingBackend, recordVisit } from './services/api';

const SPORT_LABELS: Record<string, { icon: string; label: string }> = {
  football: { icon: '🏈', label: 'Football' },
  baseball: { icon: '⚾', label: 'Baseball' },
  hockey:   { icon: '🏒', label: 'Hockey' },
};

const NeuroIQComingSoon: FunctionComponent = () => {
  const { sport = '' } = useParams<{ sport: string }>();
  const meta = SPORT_LABELS[sport] ?? { icon: '🏆', label: sport.charAt(0).toUpperCase() + sport.slice(1) };
  return (
    <div style={{ padding: '64px 32px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: 56, marginBottom: 20 }}>{meta.icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0F2042', marginBottom: 10 }}>
        {meta.label} Performance Marker
      </div>
      <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>
        This module is coming soon. We're building sport-specific performance tracking for {meta.label.toLowerCase()} — check back later.
      </div>
    </div>
  );
};

const App: FunctionComponent = () => {
  useEffect(() => {
    if (process.env.REACT_APP_VERBOSE === 'true') {
      console.log('[App] .env loaded', {
        REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
        REACT_APP_VERBOSE: process.env.REACT_APP_VERBOSE,
        REACT_APP_DEPLOY_MODE: process.env.REACT_APP_DEPLOY_MODE,
      });
    }
    pingBackend().catch(() => {});
    recordVisit().catch(() => {});
  }, []);

  return (
    <Provider store={store}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={window.location.hostname.startsWith('neuroiq') ? <Navigate to="/login" replace /> : <Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/admin/portal" element={<AuthGuard><AppShellWrapper><AdminControlCenter /></AppShellWrapper></AuthGuard>} />
          <Route path="/admin/users" element={<AuthGuard><Navigate to="/admin/portal?tab=users" replace /></AuthGuard>} />
          <Route path="/admin/health" element={<AuthGuard><Navigate to="/admin/portal?tab=health" replace /></AuthGuard>} />
          <Route path="/admin/agent" element={<AuthGuard><Navigate to="/admin/portal?tab=agent" replace /></AuthGuard>} />
          <Route path="/admin/chyps" element={<AuthGuard><Navigate to="/admin/portal?tab=chyps" replace /></AuthGuard>} />
          {/* Marketing Portal (tabbed brochures): super admin only. For public access, render without guards. */}
          <Route
            path="/marketing/clinical"
            element={
              <AuthGuard>
                <RequireSuperAdmin>
                  <AppShellWrapper>
                    <ClinicalMarketingPage />
                  </AppShellWrapper>
                </RequireSuperAdmin>
              </AuthGuard>
            }
          />
          <Route
            path="/research/portal"
            element={
              <AuthGuard>
                <RequireSuperAdmin>
                  <AppShellWrapper>
                    <ResearchPortalPage />
                  </AppShellWrapper>
                </RequireSuperAdmin>
              </AuthGuard>
            }
          />
          <Route
            path="/research/portal/:projectId"
            element={
              <AuthGuard>
                <RequireSuperAdmin>
                  <AppShellWrapper>
                    <ResearchProjectEditorPage />
                  </AppShellWrapper>
                </RequireSuperAdmin>
              </AuthGuard>
            }
          />
          <Route path="/dashboard" element={<AuthGuard><AppShellWrapper><Dashboard /></AppShellWrapper></AuthGuard>} />
          <Route path="/neuroiq" element={<AuthGuard><AppShellWrapper><NeuroIQHub /></AppShellWrapper></AuthGuard>} />
          <Route path="/neuroiq/golf" element={<AuthGuard><AppShellWrapper><GolfMarker /></AppShellWrapper></AuthGuard>} />
          <Route path="/neuroiq/:sport" element={<AuthGuard><AppShellWrapper><NeuroIQComingSoon /></AppShellWrapper></AuthGuard>} />
          <Route path="/clinical-token" element={<AuthGuard><AppShellWrapper><ModalityPlaceholder /></AppShellWrapper></AuthGuard>} />
          <Route path="/modalities/biomarker" element={<AuthGuard><AppShellWrapper><BiomarkerCenterPage /></AppShellWrapper></AuthGuard>} />
          <Route path="/modalities/symptoms" element={<AuthGuard><AppShellWrapper><SymptomsProfilePage /></AppShellWrapper></AuthGuard>} />
          <Route path="/modalities/:slug" element={<AuthGuard><AppShellWrapper><ModalityPlaceholder /></AppShellWrapper></AuthGuard>} />
          <Route path="/homework" element={<AuthGuard><AppShellWrapper><HomeworkPage /></AppShellWrapper></AuthGuard>} />
          <Route path="/settings" element={<AuthGuard><AppShellWrapper><Settings /></AppShellWrapper></AuthGuard>} />
          <Route path="/feedback" element={<AuthGuard><AppShellWrapper><Feedback /></AppShellWrapper></AuthGuard>} />
          <Route path="/chyps/:token" element={<ErrorBoundary><ChypsQuestionnaire /></ErrorBoundary>} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          {process.env.REACT_APP_DEPLOY_MODE === 'DEV' && <Route path="/logo-preview" element={<LogoPreview />} />}
          {process.env.REACT_APP_DEPLOY_MODE === 'DEV' && <Route path="/video-demo-library" element={<VideoDemoLibrary />} />}
          {process.env.REACT_APP_DEPLOY_MODE === 'DEV' && <Route path="/video-redactor" element={<VideoRedactor />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <SessionRecorder />
      </BrowserRouter>
    </Provider>
  );
};

export default App;
